<template>
  <div
    class="grid-view__rows"
    :style="{
      transform: `translateY(${rowsTop}px) translateX(${leftOffset || 0}px)`,
      left: (includeGroupBy ? activeGroupByWidth : 0) + 'px',
    }"
  >
    <template
      v-for="(row, index) in rows"
      :key="`row-${row._.persistentId}`"
    >
      <template v-if="!includeGroupBy">
        <template
          v-for="(emptyGroup, emptyIndex) in emptyGroupsBefore[row.id] || []"
          :key="`empty-group-${row.id}-${emptyIndex}`"
        >
          <GridViewGroupBand
            :group-by="emptyGroup.groupBy"
            :field="emptyGroup.field"
            :value="emptyGroup.value"
            :count="emptyGroup.count"
            :can-add-row="canAddRow"
            :collapsed="isGroupCollapsed(emptyGroup.value)"
            @toggle-collapse="toggleCollapse(emptyGroup.value)"
            @add-row-in-group="openGhost($event)"
          ></GridViewGroupBand>
          <GridViewGroupColumnsHeader
            v-if="!isGroupCollapsed(emptyGroup.value)"
            :view="view"
            :database="database"
            :visible-fields="visibleFields"
            :field-widths="fieldWidths"
            :include-row-details="includeRowDetails"
            :group="emptyGroup"
            :can-add-row="canAddRow"
            :read-only="readOnly"
            :store-prefix="storePrefix"
            :all-fields-in-table="allFieldsInTable"
            @add-row-in-group="openGhost($event)"
            @field-created="$emit('field-created', $event)"
            @field-updated="$emit('field-updated', $event)"
            @field-deleted="$emit('field-deleted')"
            @refresh="$emit('refresh')"
          ></GridViewGroupColumnsHeader>
          <div
            v-if="isAddingToGroup(emptyGroup.value)"
            class="grid-view__ghost-row"
          >
            <input
              ref="ghostInput"
              v-model="addingName"
              class="grid-view__ghost-row-input"
              :placeholder="$t('gridView.newTaskPlaceholder')"
              @keydown.stop
              @keydown.enter.prevent="confirmGhost()"
              @keydown.tab.prevent="tabGhost"
              @keydown.esc.prevent="cancelGhost"
              @blur="confirmGhost()"
            />
          </div>
          <a
            v-else-if="canAddRow && !isGroupCollapsed(emptyGroup.value)"
            class="grid-view__group-add-task"
            @click.stop="openGhost(emptyGroup)"
          >
            <i class="iconoir-plus"></i>
            {{ $t('gridView.addRowInGroup') }}
          </a>
        </template>
      </template>
      <template
        v-if="!includeGroupBy && groupStartRows[row.id] !== undefined"
      >
        <GridViewGroupBand
          :group-by="groupStartRows[row.id].groupBy"
          :field="groupStartRows[row.id].field"
          :value="groupStartRows[row.id].value"
          :count="groupStartRows[row.id].count"
          :can-add-row="canAddRow"
          :collapsed="isGroupCollapsed(groupStartRows[row.id].value)"
          @toggle-collapse="toggleCollapse(groupStartRows[row.id].value)"
          @add-row-in-group="openGhost($event)"
        ></GridViewGroupBand>
        <GridViewGroupColumnsHeader
          v-if="!isGroupCollapsed(groupStartRows[row.id].value)"
          :view="view"
          :database="database"
          :visible-fields="visibleFields"
          :field-widths="fieldWidths"
          :include-row-details="includeRowDetails"
          :group="groupStartRows[row.id]"
          :can-add-row="canAddRow"
          :read-only="readOnly"
          :store-prefix="storePrefix"
          :all-fields-in-table="allFieldsInTable"
          @add-row-in-group="openGhost($event)"
          @field-created="$emit('field-created', $event)"
          @field-updated="$emit('field-updated', $event)"
          @field-deleted="$emit('field-deleted')"
          @refresh="$emit('refresh')"
        ></GridViewGroupColumnsHeader>
      </template>
      <GridViewRow
        v-if="!isRowCollapsed(row)"
        :group-end="rowsAtEndOfGroups.has(row.id)"
        :subtasks-expanded="!!expandedSubtasks[row.id]"
        :left-offset="leftOffset || 0"
        :view="view"
        :workspace-id="workspaceId"
      :row="row"
      :rendered-fields="renderedFields"
      :visible-fields="visibleFields"
      :all-visible-fields="allVisibleFields"
      :all-fields-in-table="allFieldsInTable"
      :field-widths="fieldWidths"
      :include-row-details="includeRowDetails"
      :include-group-by="includeGroupBy"
      :decorations-by-place="decorationsByPlace"
      :read-only="readOnly"
      :can-drag="
        canDrag &&
        (bandField !== null ||
          (view.sortings.length === 0 && activeGroupBys.length === 0))
      "
      :store-prefix="storePrefix"
      :row-identifier-type="view.row_identifier_type"
      :count="index + rowsStartIndex + bufferStartIndex + 1"
        @toggle-subtasks="toggleSubtasks($event)"
        @expand-subtasks="setSubtasksExpanded($event, true)"
        @collapse-subtasks="setSubtasksExpanded($event, false)"
      @update="$emit('update', $event)"
      @paste="$emit('paste', $event)"
      @edit="$emit('edit', $event)"
      @cell-mousedown-left="$emit('cell-mousedown-left', $event)"
      @cell-mouseover="$emit('cell-mouseover', $event)"
      @cell-mouseup-left="$emit('cell-mouseup-left', $event)"
      @cell-shift-click="$emit('cell-shift-click', $event)"
      @cell-selected="$emit('cell-selected', $event)"
      @selected="$emit('selected', $event)"
      @unselected="$emit('unselected', $event)"
      @select="$emit('select', $event)"
      @unselect="$emit('unselect', $event)"
      @select-next="$emit('select-next', $event)"
      @add-row-after="$emit('add-row-after', $event)"
      @edit-modal="$emit('edit-modal', $event)"
      @refresh-row="$emit('refresh-row', $event)"
        @row-dragging="$emit('row-dragging', $event)"
        @row-hover="$emit('row-hover', $event)"
        @row-context="$emit('row-context', $event)"
      />
      <div
        v-if="
          !includeGroupBy &&
          groupEndRows[row.id] !== undefined &&
          isAddingToGroup(groupEndRows[row.id].value)
        "
        class="grid-view__ghost-row"
      >
        <input
          ref="ghostInput"
          v-model="addingName"
          class="grid-view__ghost-row-input"
          :placeholder="$t('gridView.newTaskPlaceholder')"
          @keydown.stop
          @keydown.enter.prevent="confirmGhost()"
          @keydown.tab.prevent="tabGhost"
          @keydown.esc.prevent="cancelGhost"
          @blur="confirmGhost()"
        />
      </div>
      <a
        v-else-if="
          !includeGroupBy &&
          canAddRow &&
          groupEndRows[row.id] !== undefined &&
          !isRowCollapsed(row)
        "
        class="grid-view__group-add-task"
        @click.stop="openGhost(groupEndRows[row.id])"
      >
        <i class="iconoir-plus"></i>
        {{ $t('gridView.addRowInGroup') }}
      </a>
      <template v-if="!includeGroupBy && trailingAfterRowId === row.id">
        <template
          v-for="(emptyGroup, emptyIndex) in trailingEmptyGroups"
          :key="`trailing-group-${emptyIndex}`"
        >
          <GridViewGroupBand
            :group-by="emptyGroup.groupBy"
            :field="emptyGroup.field"
            :value="emptyGroup.value"
            :count="emptyGroup.count"
            :can-add-row="canAddRow"
            :collapsed="isGroupCollapsed(emptyGroup.value)"
            @toggle-collapse="toggleCollapse(emptyGroup.value)"
            @add-row-in-group="openGhost($event)"
          ></GridViewGroupBand>
          <GridViewGroupColumnsHeader
            v-if="!isGroupCollapsed(emptyGroup.value)"
            :view="view"
            :database="database"
            :visible-fields="visibleFields"
            :field-widths="fieldWidths"
            :include-row-details="includeRowDetails"
            :group="emptyGroup"
            :can-add-row="canAddRow"
            :read-only="readOnly"
            :store-prefix="storePrefix"
            :all-fields-in-table="allFieldsInTable"
            @add-row-in-group="openGhost($event)"
            @field-created="$emit('field-created', $event)"
            @field-updated="$emit('field-updated', $event)"
            @field-deleted="$emit('field-deleted')"
            @refresh="$emit('refresh')"
          ></GridViewGroupColumnsHeader>
          <div
            v-if="isAddingToGroup(emptyGroup.value)"
            class="grid-view__ghost-row"
          >
            <input
              ref="ghostInput"
              v-model="addingName"
              class="grid-view__ghost-row-input"
              :placeholder="$t('gridView.newTaskPlaceholder')"
              @keydown.stop
              @keydown.enter.prevent="confirmGhost()"
              @keydown.tab.prevent="tabGhost"
              @keydown.esc.prevent="cancelGhost"
              @blur="confirmGhost()"
            />
          </div>
          <a
            v-else-if="canAddRow && !isGroupCollapsed(emptyGroup.value)"
            class="grid-view__group-add-task"
            @click.stop="openGhost(emptyGroup)"
          >
            <i class="iconoir-plus"></i>
            {{ $t('gridView.addRowInGroup') }}
          </a>
        </template>
      </template>
    </template>
    <!-- Tabela sem nenhuma linha: todos os grupos são vazios e são
         renderizados aqui, já que não existe linha para ancorá-los. -->
    <template v-if="!includeGroupBy && rows.length === 0">
      <template
        v-for="(emptyGroup, emptyIndex) in trailingEmptyGroups"
        :key="`trailing-group-${emptyIndex}`"
      >
        <GridViewGroupBand
          :group-by="emptyGroup.groupBy"
          :field="emptyGroup.field"
          :value="emptyGroup.value"
          :count="emptyGroup.count"
          :can-add-row="canAddRow"
          :collapsed="isGroupCollapsed(emptyGroup.value)"
          @toggle-collapse="toggleCollapse(emptyGroup.value)"
          @add-row-in-group="openGhost($event)"
        ></GridViewGroupBand>
        <GridViewGroupColumnsHeader
          v-if="!isGroupCollapsed(emptyGroup.value)"
          :view="view"
          :database="database"
          :visible-fields="visibleFields"
          :field-widths="fieldWidths"
          :include-row-details="includeRowDetails"
          :group="emptyGroup"
          :can-add-row="canAddRow"
          :read-only="readOnly"
          :store-prefix="storePrefix"
          :all-fields-in-table="allFieldsInTable"
          @add-row-in-group="openGhost($event)"
          @field-created="$emit('field-created', $event)"
          @refresh="$emit('refresh')"
        ></GridViewGroupColumnsHeader>
        <div
          v-if="isAddingToGroup(emptyGroup.value)"
          class="grid-view__ghost-row"
        >
          <input
            ref="ghostInput"
            v-model="addingName"
            class="grid-view__ghost-row-input"
            :placeholder="$t('gridView.newTaskPlaceholder')"
            @keydown.stop
            @keydown.enter.prevent="confirmGhost()"
            @keydown.tab.prevent="tabGhost"
            @keydown.esc.prevent="cancelGhost"
            @blur="confirmGhost()"
          />
        </div>
        <a
          v-else-if="canAddRow && !isGroupCollapsed(emptyGroup.value)"
          class="grid-view__group-add-task"
          @click.stop="openGhost(emptyGroup)"
        >
          <i class="iconoir-plus"></i>
          {{ $t('gridView.addRowInGroup') }}
        </a>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import GridViewRow from '@baserow/modules/database/components/view/grid/GridViewRow'
import GridViewGroupBand from '@baserow/modules/database/components/view/grid/GridViewGroupBand'
import GridViewGroupColumnsHeader from '@baserow/modules/database/components/view/grid/GridViewGroupColumnsHeader'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'

export default {
  name: 'GridViewRows',
  components: { GridViewRow, GridViewGroupBand, GridViewGroupColumnsHeader },
  mixins: [gridViewHelpers],
  props: {
    /**
     * The visible fields that are within the viewport. The other ones are not rendered
     * for performance reasons.
     */
    renderedFields: {
      type: Array,
      required: true,
    },
    /**
     * The fields that are chosen to be visible within the view.
     */
    visibleFields: {
      type: Array,
      required: true,
    },
    allVisibleFields: {
      type: Array,
      required: true,
    },
    /**
     * All the fields in the table, regardless of the visibility, or whether they
     * should be rendered.
     */
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    decorationsByPlace: {
      type: Object,
      required: true,
    },
    leftOffset: {
      type: Number,
      required: false,
      default: 0,
    },
    view: {
      type: Object,
      required: true,
    },
    /**
     * Origem Flow: necessário pelo cabeçalho de colunas por grupo (checagem
     * de permissão do resize de coluna).
     */
    database: {
      type: Object,
      required: false,
      default: null,
    },
    includeRowDetails: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeGroupBy: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    workspaceId: {
      type: Number,
      required: true,
    },
    rowsAtEndOfGroups: {
      type: Set,
      required: true,
    },
    /**
     * Origem Flow: mapa rowId -> { groupBy, field, value, count } das linhas
     * que iniciam um grupo, usado para renderizar a banda de grupo estilo
     * ClickUp antes da primeira linha de cada grupo (só na seção direita).
     */
    groupStartRows: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Origem Flow: mapa rowId -> payload de "+ Adicionar tarefa" das linhas
     * que fecham um grupo — renderiza a linha persistente de adicionar
     * tarefa no fim de cada grupo (estilo ClickUp).
     */
    groupEndRows: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Origem Flow: grupos sem nenhuma linha (opções do single select), a
     * renderizar antes da banda da linha indicada (`before`) ou depois da
     * última linha (`trailing`) — assim todos os status aparecem sempre,
     * como no ClickUp.
     */
    emptyGroupsBefore: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    trailingEmptyGroups: {
      type: Array,
      required: false,
      default: () => [],
    },
    trailingAfterRowId: {
      type: Number,
      required: false,
      default: null,
    },
    canAddRow: {
      type: Boolean,
      required: false,
      default: false,
    },
    canDrag: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update',
    'paste',
    'edit',
    'cell-mousedown-left',
    'cell-mouseover',
    'cell-mouseup-left',
    'cell-shift-click',
    'cell-selected',
    'selected',
    'unselected',
    'select',
    'unselect',
    'select-next',
    'add-row-after',
    'edit-modal',
    'refresh-row',
    'row-dragging',
    'row-hover',
    'row-context',
    'add-row-in-group',
    'field-created',
    'field-updated',
    'field-deleted',
    'refresh',
  ],
  data() {
    return {
      /**
       * Origem Flow: grupos recolhidos pela setinha da banda (estilo
       * ClickUp), chaveados pelo id da opção do single select ('__null__'
       * para linhas sem valor). Persistido em localStorage por view.
       */
      collapsedGroups: {},
      /**
       * Origem Flow: linhas com o bloco de subtarefas aberto, chaveadas
       * pelo id. Vive aqui (e não no GridViewRow) porque a janela virtual
       * recria os componentes de linha — com estado local o bloco fechava
       * sozinho a cada re-render.
       */
      expandedSubtasks: {},
      /**
       * Origem Flow: "ghost input" de nova tarefa — payload do grupo em que
       * o usuário está digitando (ou null) e o título parcial. A tarefa só
       * é criada com título preenchido (Enter ou clicar fora); Esc/vazio
       * não cria nada.
       */
      addingGroup: null,
      addingName: '',
    }
  },
  computed: {
    fieldWidths() {
      const fieldWidths = {}
      this.visibleFields.forEach((field) => {
        fieldWidths[field.id] = this.getFieldWidth(field)
      })
      return fieldWidths
    },
    /**
     * Campo do group by único no modo banda; null fora dele (o collapse só
     * existe no modo banda).
     */
    bandField() {
      if (this.includeGroupBy || this.activeGroupBys.length !== 1) {
        return null
      }
      const groupBy = this.activeGroupBys[0]
      return this.allFieldsInTable.find((f) => f.id === groupBy.field) || null
    },
    collapseStorageKey() {
      return `origemCollapsedGroups:${this.view.id}`
    },
    rows() {
      return this.$store.getters[this.storePrefix + 'view/grid/getRows']
    },
    rowsTop() {
      return this.$store.getters[this.storePrefix + 'view/grid/getRowsTop']
    },
    rowsStartIndex() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/getRowsStartIndex'
      ]
    },
    rowsEndIndex() {
      return this.$store.getters[this.storePrefix + 'view/grid/getRowsEndIndex']
    },
    bufferStartIndex() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/getBufferStartIndex'
      ]
    },
    activeGroupBys() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/getActiveGroupBys'
      ]
    },
  },
  mounted() {
    try {
      const stored = window.localStorage.getItem(this.collapseStorageKey)
      if (stored) {
        this.collapsedGroups = JSON.parse(stored)
      }
    } catch (e) {
      // localStorage indisponível (ex.: modo privado) — segue sem persistir.
    }
    // Botão "+ Tarefa" do topo: abre o ghost do primeiro grupo (só a seção
    // com os detalhes da linha reage — é onde o input aparece).
    this.$openFirstGhost = () => this.openFirstGhost()
    this.$bus.$on('origem-open-first-ghost', this.$openFirstGhost)
  },
  beforeUnmount() {
    this.$bus.$off('origem-open-first-ghost', this.$openFirstGhost)
  },
  methods: {
    /**
     * O bloco de subtarefas só fecha pela setinha (ou pelo contador) —
     * clicar fora não fecha nada.
     */
    toggleSubtasks(row) {
      this.setSubtasksExpanded(row, !this.expandedSubtasks[row.id])
    },
    setSubtasksExpanded(row, value) {
      const expanded = { ...this.expandedSubtasks }
      if (value) {
        expanded[row.id] = true
      } else {
        delete expanded[row.id]
      }
      this.expandedSubtasks = expanded
    },
    groupCollapseKey(value) {
      return value && value.id !== undefined && value.id !== null
        ? String(value.id)
        : '__null__'
    },
    isGroupCollapsed(value) {
      return Boolean(this.collapsedGroups[this.groupCollapseKey(value)])
    },
    isRowCollapsed(row) {
      if (this.bandField === null) {
        return false
      }
      return this.isGroupCollapsed(row[`field_${this.bandField.id}`])
    },
    toggleCollapse(value) {
      const key = this.groupCollapseKey(value)
      if (this.collapsedGroups[key]) {
        delete this.collapsedGroups[key]
      } else {
        this.collapsedGroups[key] = true
      }
      try {
        window.localStorage.setItem(
          this.collapseStorageKey,
          JSON.stringify(this.collapsedGroups)
        )
      } catch (e) {
        // Sem persistência, o estado ainda vale para a sessão atual.
      }
    },
    isAddingToGroup(value) {
      return (
        this.addingGroup !== null &&
        this.groupCollapseKey(this.addingGroup.value) ===
          this.groupCollapseKey(value)
      )
    },
    /**
     * Abre o input de nova tarefa do PRIMEIRO grupo visível (banda vazia
     * antes da primeira linha, grupo da primeira linha ou primeiro grupo
     * vazio da tabela sem linhas).
     */
    openFirstGhost() {
      if (!this.includeRowDetails || this.includeGroupBy || !this.canAddRow) {
        return
      }
      const groupBy = this.activeGroupBys[0]
      const field = groupBy && this.bandField
      if (!groupBy || !field) {
        return
      }
      const rows = this.rows
      const emptyBefore = rows.length
        ? this.emptyGroupsBefore[rows[0].id] || []
        : []
      if (emptyBefore.length) {
        this.openGhost(emptyBefore[0])
      } else if (rows.length) {
        this.openGhost({
          groupBy,
          field,
          value: rows[0][`field_${groupBy.field}`],
        })
      } else if (this.trailingEmptyGroups.length) {
        this.openGhost(this.trailingEmptyGroups[0])
      }
    },
    openGhost(payload) {
      if (!this.canAddRow) {
        return
      }
      // Célula selecionada segue ouvindo o teclado global (digitar abriria
      // o dropdown dela e roubaria o foco do ghost input) — desseleciona.
      this.$store.dispatch(this.storePrefix + 'view/grid/setSelectedCell', {
        rowId: -1,
        fieldId: -1,
        fields: this.allFieldsInTable,
      })
      // Grupo recolhido: expande para o input aparecer no lugar certo.
      const key = this.groupCollapseKey(payload.value)
      if (this.collapsedGroups[key]) {
        this.toggleCollapse(payload.value)
      }
      this.addingGroup = payload
      this.addingName = ''
      this.$nextTick(() => {
        const ref = this.$refs.ghostInput
        const input = Array.isArray(ref) ? ref[0] : ref
        if (input) {
          input.focus()
        }
      })
    },
    confirmGhost(focusFields = false) {
      if (this.addingGroup === null) {
        return
      }
      const name = this.addingName.trim()
      const payload = this.addingGroup
      this.addingGroup = null
      this.addingName = ''
      if (name !== '') {
        this.$emit('add-row-in-group', {
          ...payload,
          primaryValue: name,
          focusFields,
        })
      }
    },
    /**
     * Origem Flow: Tab no ghost input cria a tarefa e cai direto no próximo
     * campo (Responsável) com o editor já aberto — criação fluida só no
     * teclado. Com o título vazio o Tab não faz nada (não cria linha).
     */
    tabGhost() {
      if (this.addingName.trim() !== '') {
        this.confirmGhost(true)
      }
    },
    cancelGhost() {
      this.addingGroup = null
      this.addingName = ''
    },
  },
}
</script>
